import {
  Alert,
  Button,
  Container,
  ContentLayout,
  ExpandableSection,
  FormField,
  Header,
  Input,
  SpaceBetween,
} from '@cloudscape-design/components';
import { LabelPrinterIds } from '@parallel-fluidics/shared/constants';
import PrinterSelect from 'features/labelPrinters/components/PrinterSelect';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Formik } from 'formik';
import React, {
  useState,
} from 'react';
import { toast } from 'sonner';
import * as Yup from 'yup';

const validationSchema = Yup.object().shape({
  count: Yup.number()
    .required('Please enter a number between 1 and 500')
    .min(1, 'Number must be at least 1')
    .max(500, 'Number cannot exceed 500'),
});

export default function PrintTravelerLabelsPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  return (
    <ContentLayout header={<Header variant="h1">Print Traveler Labels</Header>}>
      <Container>
        <Formik
          initialValues={{ count: '', printer: LabelPrinterIds.CLEAN_ROOM }}
          validationSchema={validationSchema}
          onSubmit={async (values) => {
            try {
              setLoading(true);
              setError(null);
              const printTravelerLabels = httpsCallable(getFunctions(), 'printTravelerLabelsV2');
              await printTravelerLabels({ count: values.count, printer: values.printer });
              toast.success(`Successfully sent ${values.count} label${values.count > 1 ? 's' : ''} to printer.`);
            } catch (e) {
              // Compose a friendly message + include server error/code if present
              const msg = [
                e.message,
                e.code ? `(${e.code})` : null,
                e.status ? `HTTP ${e.status}` : null,
              ].filter(Boolean).join(' ');
              setError(msg);
            } finally {
              setLoading(false);
            }
          }}
        >
          {({
            values,
            errors,
            setFieldValue,
            handleSubmit,
          }) => (
            <form onSubmit={handleSubmit}>
              <SpaceBetween size="m">
                <PrinterSelect
                  value={values.printer}
                  onChange={(printer) => setFieldValue('printer', printer)}
                />
                <FormField
                  description="QR code labels will be printed on the selected label printer."
                  label="How many traveler labels to print?"
                  errorText={errors.count}
                >
                  <Input
                    inputMode="numeric"
                    value={values.count}
                    onChange={(event) => {
                      const parsed = parseInt(event.detail.value, 10);
                      setFieldValue('count', Number.isNaN(parsed) ? '' : parsed);
                    }}
                  />
                </FormField>
                <Button type="submit" variant="primary" loading={loading}>Print Labels</Button>

                {error && (
                  <Alert
                    type="error"
                    header="Failed to print labels"
                  >
                    {error}
                  </Alert>
                )}

                <ExpandableSection headerText="What if printing fails?">
                  <div style={{ paddingLeft: 16 }}>
                    <SpaceBetween size="xxs">
                      {/* eslint-disable-next-line max-len */}
                      <li>Ensure the printer is connected to its Raspberry Pi via USB.</li>
                      <li>Confirm the printer is on — the green light should be lit.</li>
                      <li>Make sure there’s enough label paper loaded.</li>
                      {/* eslint-disable-next-line max-len */}
                      <li>If the power light blinks red after a roll change, press the printer’s power button once — the label will print, and later prints go through on their own.</li>
                      <li>No Wi-Fi is required for this printer.</li>
                      {/* eslint-disable-next-line max-len */}
                      <li>Check the Raspberry Pi is powered (it auto-starts when plugged in; look for the green light).</li>
                      <li>If the issue persists, contact the engineering team.</li>
                    </SpaceBetween>
                  </div>
                </ExpandableSection>
              </SpaceBetween>
            </form>
          )}
        </Formik>
      </Container>
    </ContentLayout>
  );
}
